import * as XLSX from 'xlsx';
import { InvoiceNotification } from '../types/invoiceNotification';

const normalizeHeader = (value: any): string => {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[._-]/g, '')
    .replace(/đ/g, 'd');
};

const findValue = (row: Record<string, any>, aliases: string[]): any => {
  const entries = Object.entries(row);
  const normalizedAliases = aliases.map(normalizeHeader);

  for (const [key, value] of entries) {
    const normalizedKey = normalizeHeader(key);
    if (normalizedAliases.includes(normalizedKey)) {
      return value;
    }
  }

  return undefined;
};

const findHeaderIndex = (rows: any[][]): number => {
  const requiredAliases = ['mahoadon', 'sohoadon', 'tenhang', 'soluong', 'dongia', 'thanhtien'];

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index] || [];
    const normalizedCells = row.map(normalizeHeader).filter(Boolean);
    const score = requiredAliases.filter(alias => normalizedCells.some(cell => cell.includes(alias))).length;

    if (score >= 3) {
      return index;
    }
  }

  return 0;
};

const isEmptyRow = (row: Record<string, any>): boolean => {
  return Object.values(row).every(value => String(value ?? '').trim() === '');
};

const toNumber = (value: any): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const normalized = String(value).replace(/,/g, '').replace(/\s/g, '');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toDisplayDate = (value: any): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) {
      return String(value);
    }
    const day = String(parsed.d).padStart(2, '0');
    const month = String(parsed.m).padStart(2, '0');
    const year = String(parsed.y);
    return `${day}/${month}/${year}`;
  }

  return String(value);
};

const toDisplayTime = (value: any): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    const totalSeconds = Math.round((value % 1) * 24 * 60 * 60);
    const hours = String(Math.floor(totalSeconds / 3600) % 24).padStart(2, '0');
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  return String(value).trim();
};

const buildNotificationContent = (invoice: InvoiceNotification): string => {
  const parts: string[] = [];

  if (invoice.store) {
    parts.push(`Cửa hàng: ${invoice.store}`);
  }
  if (invoice.tableName) {
    parts.push(`Bàn: ${invoice.tableName}`);
  }
  if (invoice.itemName) {
    parts.push(`Món: ${invoice.itemName}`);
  }
  if (invoice.quantity !== undefined) {
    parts.push(`SL: ${invoice.quantity}`);
  }
  if (invoice.lineTotal !== undefined) {
    parts.push(`Thành tiền: ${invoice.lineTotal.toLocaleString('vi-VN')} ${invoice.currency || 'VND'}`);
  }

  return parts.join(' | ');
};

export const parseExcelToInvoiceNotifications = (file: File): Promise<InvoiceNotification[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        let selectedMatrix: any[][] = [];

        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const matrix = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false,
          }) as any[][];

          if (matrix.length > selectedMatrix.length) {
            selectedMatrix = matrix;
          }
        });

        const matrix = selectedMatrix;

        if (!matrix.length) {
          resolve([]);
          return;
        }

        const headerIndex = findHeaderIndex(matrix);
        const headers = (matrix[headerIndex] || []).map((header) => String(header || '').trim());
        const bodyRows = matrix.slice(headerIndex + 1);

        const carryValues: Record<string, any> = {};
        const normalizedRows = bodyRows
          .map((rowArray) => {
            const rowObject: Record<string, any> = {};
            headers.forEach((header, columnIndex) => {
              const cellValue = rowArray[columnIndex];
              const trimmed = String(cellValue ?? '').trim();

              if (trimmed !== '') {
                rowObject[header] = cellValue;
                carryValues[header] = cellValue;
              } else {
                rowObject[header] = carryValues[header] ?? '';
              }
            });

            return rowObject;
          })
          .filter((row) => !isEmptyRow(row));

        const invoices = normalizedRows
          .map((row, index) => {
            const stt = findValue(row, ['STT']);
            const store = findValue(row, ['Cửa hàng', 'Cua hang', 'Store']);
            const shiftCode = findValue(row, ['Mã ca', 'Ma ca', 'Shift code']);
            const invoiceCodeRaw = findValue(row, ['Mã hóa đơn', 'Ma hoa don', 'Invoice code', 'Invoice no']);
            const billNumber = findValue(row, ['Số hóa đơn', 'So hoa don', 'Bill number']);
            const eInvoiceNumber = findValue(row, ['Số hóa đơn điện tử', 'So hoa don dien tu', 'E-invoice']);
            const checkInDate = findValue(row, ['Ngày vào', 'Ngay vao', 'Check in date']);
            const checkInTime = findValue(row, ['Giờ vào', 'Gio vao', 'Check in time']);
            const checkOutDate = findValue(row, ['Ngày ra', 'Ngay ra', 'Check out date']);
            const checkOutTime = findValue(row, ['Giờ ra', 'Gio ra', 'Check out time']);
            const vatIssuedAt = findValue(row, ['Thời gian xuất hóa đơn VAT', 'Thoi gian xuat hoa don VAT', 'VAT issued at']);
            const tableName = findValue(row, ['Bàn', 'Ban', 'Table']);
            const status = findValue(row, ['Trạng thái', 'Trang thai', 'Status']);
            const rootInvoiceCode = findValue(row, ['Mã hóa đơn gốc', 'Ma hoa don goc', 'Root invoice']);
            const itemName = findValue(row, ['Tên hàng', 'Ten hang', 'Item name', 'Sản phẩm']);
            const quantity = toNumber(findValue(row, ['Số lượng', 'So luong', 'Quantity']));
            const unit = findValue(row, ['Đơn vị', 'Don vi', 'Unit']);
            const unitPrice = toNumber(findValue(row, ['Đơn giá', 'Don gia', 'Unit price', 'Đơn vị đơn', 'Don vi don']));
            const lineTotal = toNumber(findValue(row, ['Thành tiền', 'Thanh tien', 'Line total', 'Tổng tiền dòng']));
            const vatRate = toNumber(findValue(row, ['VAT', 'VAT (%)', 'Thuế VAT']));
            const note = findValue(row, ['Ghi chú', 'Ghi chu', 'Note']);

            const invoiceCode = String(invoiceCodeRaw || billNumber || rootInvoiceCode || '').trim();
            const itemNameText = String(itemName || '').trim();

            if (!invoiceCode || !itemNameText) {
              return null;
            }

            const lineKey = `${invoiceCode}__${itemNameText}__${quantity ?? ''}__${unitPrice ?? ''}__${index + 1}`;
            const resolvedLineTotal = lineTotal ?? (((quantity || 0) * (unitPrice || 0)) || undefined);

            const invoice: InvoiceNotification = {
              lineKey,
              stt: stt ? String(stt).trim() : String(index + 1),
              store: store ? String(store).trim() : '',
              shiftCode: shiftCode ? String(shiftCode).trim() : '',
              invoiceCode,
              billNumber: billNumber ? String(billNumber).trim() : '',
              eInvoiceNumber: eInvoiceNumber ? String(eInvoiceNumber).trim() : '',
              checkInDate: toDisplayDate(checkInDate) || '',
              checkInTime: toDisplayTime(checkInTime) || '',
              checkOutDate: toDisplayDate(checkOutDate) || '',
              checkOutTime: toDisplayTime(checkOutTime) || '',
              vatIssuedAt: vatIssuedAt ? String(vatIssuedAt).trim() : '',
              tableName: tableName ? String(tableName).trim() : '',
              status: status ? String(status).trim() : 'pending',
              rootInvoiceCode: rootInvoiceCode ? String(rootInvoiceCode).trim() : '',
              itemName: itemNameText,
              quantity,
              unit: unit ? String(unit).trim() : '',
              unitPrice,
              lineTotal: resolvedLineTotal,
              vatRate,
              note: note ? String(note).trim() : '',
              orderCode: shiftCode ? String(shiftCode).trim() : '',
              saleDate: toDisplayDate(checkInDate) || '',
              totalAmount: resolvedLineTotal,
              currency: 'VND',
              channel: store ? String(store).trim() : '',
              notificationTitle: `Hóa đơn ${invoiceCode} - ${itemNameText}`,
              notificationContent: '',
              rawData: row,
              isActive: true,
            };

            invoice.notificationContent = buildNotificationContent(invoice);
            return invoice;
          })
          .filter((item): item is InvoiceNotification => Boolean(item));

        resolve(invoices);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};
